import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, ShieldAlert, ShieldQuestion,
  Play, RefreshCcw,
} from "lucide-react";
import {
  startCharacterCheck,
  getCharacterCheck,
} from "@/lib/api";

/**
 * Per-storyboard Vision-LLM audit that verifies every scene's
 * characters match the storyboard's cast references (Character
 * Library entries OR channel-analysis characters). Renders as a
 * collapsible panel above the scene grid — collapsed by default
 * until the user runs their first scan, then defaults to open so
 * the results stay in view.
 */
export default function CharacterConsistencyPanel({ storyboardId, scenes }) {
  const [state, setState] = useState(null); // {status, results, summary, cast}
  const [starting, setStarting] = useState(false);
  const [open, setOpen] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await getCharacterCheck(storyboardId);
      setState(d);
      // Auto-open when there are results to look at.
      if (d?.status && d.status !== "idle") setOpen(true);
      return d;
    } catch (e) {
      // 404 is expected before the first scan is started.
      if (e?.response?.status !== 404) {
        toast.error(e?.response?.data?.detail || "Couldn't load check");
      }
      return null;
    }
  }, [storyboardId]);

  useEffect(() => { load(); }, [load]);

  // Poll while a scan is running. Stops itself when the server
  // reports `complete` or `failed` so we don't hammer the endpoint.
  useEffect(() => {
    const isRunning = state?.status === "running";
    if (!isRunning) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    if (pollRef.current) return undefined;
    pollRef.current = setInterval(load, 3500);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [state?.status, load]);

  const onStart = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const d = await startCharacterCheck(storyboardId);
      // Immediately reflect running state in the UI without waiting
      // for the first poll — feels snappier.
      setState((prev) => ({
        ...(prev || {}),
        status: "running",
        started_at: d?.started_at || new Date().toISOString(),
        results: prev?.results || scenes.map((s, i) => ({
          scene_id: s.scene_id, scene_idx: i,
          verdict: "pending", checks: [],
        })),
      }));
      setOpen(true);
      toast.success(d?.already_running ? "Check already in progress" : "Character check started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start check");
    } finally {
      setStarting(false);
    }
  };

  const status = state?.status || "idle";
  const results = state?.results || [];
  const summary = state?.summary || null;
  const cast = state?.cast || [];
  const running = status === "running";

  return (
    <div
      data-testid="character-consistency-panel"
      className="border border-zinc-900 rounded-sm bg-zinc-950/60"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-zinc-900/40 transition-colors"
        data-testid="character-consistency-toggle"
      >
        <StatusIcon status={status} summary={summary} />
        <div className="flex-1 text-left">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-yellow-400">
            character consistency
          </div>
          <div className="text-[12px] text-zinc-400 mt-0.5">
            <StatusLine
              status={status}
              summary={summary}
              cast={cast}
              healing={state?.healing}
              healingRound={state?.healing_round}
            />
          </div>
        </div>
        <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em]">
          {open ? "hide" : "show"}
        </div>
      </button>

      {open && (
        <div className="border-t border-zinc-900 p-4 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={onStart}
              disabled={starting || running}
              data-testid="character-consistency-run"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 border border-yellow-400/60 hover:border-yellow-400 hover:bg-yellow-400 hover:text-black text-yellow-400 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {starting || running
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : status === "complete"
                  ? <RefreshCcw className="w-3 h-3" />
                  : <Play className="w-3 h-3" />}
              {running
                ? "checking…"
                : (status === "complete" ? "re-check" : "run check & auto-fix")}
            </button>
            {cast.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                  cast:
                </div>
                {cast.map((c) => (
                  <div
                    key={c.name}
                    className="flex items-center gap-1.5 bg-zinc-900/60 border border-zinc-800 rounded-sm px-2 py-1"
                    data-testid={`character-consistency-cast-${(c.name || "").toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {c.reference_url && (
                      <img
                        src={c.reference_url}
                        alt=""
                        className="w-5 h-5 rounded-sm object-cover"
                        loading="lazy"
                      />
                    )}
                    <span className="font-mono text-[10px] text-zinc-300">
                      {c.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {state?.error && (
            <div className="text-[12px] text-red-300 border border-red-500/30 bg-red-500/5 rounded-sm p-2 font-mono">
              {state.error}
            </div>
          )}

          {results.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {results.map((r) => (
                <SceneResultCard key={r.scene_id || r.scene_idx} result={r} />
              ))}
            </div>
          )}

          {status === "idle" && (
            <p className="text-[12px] text-zinc-500 leading-relaxed">
              Click <strong className="text-zinc-300">run check</strong> to
              audit every scene against your character references. Vision
              LLM confirms Bella / Mom / etc. match their portraits and
              flags any drift so you can re-render before publishing.
            </p>
          )}
        </div>
      )}
    </div>
  );
}


function StatusIcon({ status, summary }) {
  if (status === "running") {
    return <Loader2 className="w-5 h-5 animate-spin text-yellow-400" />;
  }
  if (status === "failed") {
    return <ShieldAlert className="w-5 h-5 text-red-400" />;
  }
  if (status === "complete") {
    if (summary?.drift > 0) {
      return <ShieldAlert className="w-5 h-5 text-red-400" />;
    }
    if (summary?.match > 0) {
      return <ShieldCheck className="w-5 h-5 text-emerald-400" />;
    }
    return <ShieldQuestion className="w-5 h-5 text-zinc-500" />;
  }
  return <ShieldQuestion className="w-5 h-5 text-zinc-500" />;
}

function StatusLine({ status, summary, cast, healing, healingRound }) {
  if (status === "idle") {
    return (
      <>Verify every scene&apos;s characters match your{" "}
        {cast.length > 0
          ? `${cast.length}-character cast`
          : "reference portraits"} — drifted scenes auto-heal.</>
    );
  }
  if (status === "running") {
    if (healing) {
      return (
        <>Auto-fixing drifted scenes (round {healingRound || 1}/2)…</>
      );
    }
    return <>Checking scenes against {cast.length} character reference{cast.length === 1 ? "" : "s"}…</>;
  }
  if (status === "failed") {
    return <>Check failed — click re-check to retry.</>;
  }
  if (status === "complete" && summary) {
    const bits = [];
    if (summary.match) bits.push(`${summary.match} match`);
    if (summary.drift) {
      // Any drift still present after the scan means auto-heal ran
      // out of attempts. Surface as manual-review copy so the user
      // knows they need to intervene.
      bits.push(`${summary.drift} needs review`);
    }
    if (summary.no_reference) bits.push(`${summary.no_reference} skipped`);
    return <>{bits.join(" · ") || "No results"}.</>;
  }
  return null;
}

function SceneResultCard({ result }) {
  const { scene_idx, verdict, checks, heal_attempts, heal_exhausted } = result;
  // A drifted scene that ran out of auto-heal retries gets a
  // "needs review" tint so it stands out from actively-healing
  // scenes (which should look transient / cyan).
  const effectiveVerdict = (verdict === "drift" && heal_exhausted)
    ? "drift"
    : verdict;
  const badge = VERDICT_META[effectiveVerdict] || VERDICT_META.unknown;
  return (
    <div
      data-testid={`character-consistency-scene-${scene_idx}`}
      className={`border rounded-sm p-2.5 text-[11px] ${badge.border} ${badge.bg}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="font-mono uppercase tracking-[0.25em] text-zinc-400 text-[10px]">
          scene {Number.isInteger(scene_idx) ? scene_idx + 1 : "?"}
        </div>
        <div className={`font-mono uppercase tracking-[0.2em] text-[9px] font-bold ${badge.text}`}>
          {verdict === "healing" && heal_attempts ? `◐ auto-fix ${heal_attempts}/2` : badge.label}
          {verdict === "drift" && heal_exhausted && (
            <span className="ml-1 opacity-80">· needs review</span>
          )}
        </div>
      </div>
      {checks.length > 0 ? (
        <ul className="space-y-1">
          {checks.map((c) => (
            <li key={c.character_name} className="flex items-start gap-1.5">
              <span className={`inline-block mt-0.5 w-1.5 h-1.5 rounded-full ${CHECK_DOT[c.verdict] || CHECK_DOT.unknown}`} />
              <span className="text-zinc-300 flex-1">
                <span className="text-zinc-100 font-medium">{c.character_name}</span>
                <span className="text-zinc-500"> · {c.verdict.replace(/_/g, " ")}</span>
                {c.reason && (
                  <div className="text-zinc-500 italic text-[10px] leading-snug mt-0.5">
                    {c.reason}
                  </div>
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : verdict === "pending" ? (
        <div className="flex items-center gap-2 text-zinc-500">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>waiting…</span>
        </div>
      ) : verdict === "healing" ? (
        <div className="flex items-center gap-2 text-cyan-300">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>re-rendering to match reference…</span>
        </div>
      ) : verdict === "scan_error" || verdict === "llm_error" ? (
        <div className="text-amber-400 italic">
          {result.error || "check failed — click re-check to retry"}
        </div>
      ) : (
        <div className="text-zinc-500 italic">no characters to compare</div>
      )}
    </div>
  );
}

const VERDICT_META = {
  pending: { label: "pending", border: "border-zinc-800", bg: "bg-zinc-900/40", text: "text-zinc-500" },
  all_match: { label: "✓ match", border: "border-emerald-500/40", bg: "bg-emerald-500/5", text: "text-emerald-400" },
  drift: { label: "⚠ drift", border: "border-red-500/50", bg: "bg-red-500/10", text: "text-red-400" },
  healing: { label: "◐ auto-fixing", border: "border-cyan-500/40", bg: "bg-cyan-500/10", text: "text-cyan-300" },
  llm_error: { label: "⟳ retry", border: "border-amber-500/40", bg: "bg-amber-500/5", text: "text-amber-400" },
  scan_error: { label: "⟳ retry", border: "border-amber-500/40", bg: "bg-amber-500/5", text: "text-amber-400" },
  no_characters: { label: "no cast", border: "border-zinc-800", bg: "bg-zinc-900/40", text: "text-zinc-500" },
  no_reference: { label: "no ref", border: "border-zinc-800", bg: "bg-zinc-900/40", text: "text-zinc-500" },
  no_scene_image: { label: "no image", border: "border-zinc-800", bg: "bg-zinc-900/40", text: "text-zinc-500" },
  unknown: { label: "unknown", border: "border-zinc-800", bg: "bg-zinc-900/40", text: "text-zinc-500" },
};

const CHECK_DOT = {
  match: "bg-emerald-400",
  drift: "bg-red-400",
  missing_from_scene: "bg-amber-400",
  unknown: "bg-zinc-500",
};
